/**
 * The upload pipeline the admin sees (spec 2026-10-23 §5, §6.3), as pure
 * steps: build the form, read a status, decide what to do next, say it.
 *
 *   browser checks → POST /uploads (a signed form) → the file goes STRAIGHT to
 *   storage (XMLHttpRequest, with a progress bar) → POST /process → poll the
 *   status every 3 s until ready or failed → give up after 15 min.
 *
 * The clock is an argument everywhere, so the give-up is testable.
 */
import { VIDEO_UPLOAD_MAX_BYTES } from './contract';
import type { FinalizedMedia } from './intro-state';

export const POLL_INTERVAL_MS = 3000;
export const POLL_GIVE_UP_MS = 15 * 60 * 1000;
export const TOO_LONG_TEXT = 'The conversion is taking too long. Try again.';

/** GET /v1/admin/app-experience/uploads/{id} (backend/src/app-experience/uploads.ts UploadStatusView). */
export type UploadStatus =
  | { status: 'awaiting_upload' }
  | { status: 'processing'; percent?: number | null }
  | { status: 'ready'; media: FinalizedMedia }
  /** `retryable` is missing from an API older than this field: that means true. */
  | { status: 'failed'; error: string; retryable?: boolean };

/** What one upload shows, from the chosen file to a converted video. */
export type UploadState =
  | { phase: 'idle' }
  | { phase: 'checking'; fileName: string }
  | { phase: 'rejected'; fileName: string; errors: string[]; warnings: string[] }
  | { phase: 'uploading'; fileName: string; loaded: number; total: number; warnings: string[] }
  | { phase: 'processing'; fileName: string; percent: number | null; warnings: string[] }
  | { phase: 'failed'; fileName: string; error: string; retryable: boolean; warnings: string[] }
  | { phase: 'done'; fileName: string; warnings: string[] };

export type PollStep =
  | { kind: 'wait'; delayMs: number; percent: number | null }
  | { kind: 'ready'; media: FinalizedMedia }
  | { kind: 'failed'; error: string; retryable: boolean };

/**
 * The signed form for the storage POST: every field the server signed FIRST,
 * the file LAST. Storage reads the fields up to the file and ignores anything
 * after it, so a field after the file would fail the signature.
 */
export function buildUploadForm(fields: Record<string, string>, file: File): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  form.append('file', file, file.name);
  return form;
}

const wait = (percent: number | null): PollStep => ({ kind: 'wait', delayMs: POLL_INTERVAL_MS, percent });

export function pollDecision(status: UploadStatus, startedAt: number, now: number): PollStep {
  if (status.status === 'ready') return { kind: 'ready', media: status.media };
  if (status.status === 'failed') return { kind: 'failed', error: status.error, retryable: status.retryable !== false };
  if (now - startedAt >= POLL_GIVE_UP_MS) return { kind: 'failed', error: TOO_LONG_TEXT, retryable: true };
  const percent = status.status === 'processing' && typeof status.percent === 'number' ? status.percent : null;
  return wait(percent);
}

/**
 * A status read that FAILED. A refusal (4xx: the upload does not exist, the id
 * is not valid) will not change on the next try, so it stops with the server's
 * words. A server or network fault can pass, so the next tick tries again —
 * until the same 15-minute give-up.
 */
export function pollErrorDecision(err: { status?: number; message: string }, startedAt: number, now: number): PollStep {
  if (err.status !== undefined && err.status >= 400 && err.status < 500) return { kind: 'failed', error: err.message, retryable: true };
  if (now - startedAt >= POLL_GIVE_UP_MS) return { kind: 'failed', error: TOO_LONG_TEXT, retryable: true };
  return wait(null);
}

// ─── words ──────────────────────────────────────────────────────────────────

const MB = 1024 * 1024;
const GB = 1024 * MB;

export function formatBytes(bytes: number): string {
  if (bytes >= GB) return `${(bytes / GB).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / MB))} MB`;
}

export function uploadingText(loaded: number, total: number): string {
  if (total <= 0) return 'Uploading… 0 %';
  const pct = Math.min(100, Math.floor((loaded / total) * 100));
  return `Uploading… ${pct} % (${formatBytes(loaded)} of ${formatBytes(total)})`;
}

export function processingText(percent: number | null): string {
  return percent === null ? 'Processing…' : `Processing… ${Math.round(percent)} %`;
}

/**
 * The storage service answers a failed upload in its own terms. Turn the few
 * an admin can act on into words; for anything else, give the number (support
 * can use it) and say to try again.
 */
export function uploadErrorText(status: number, body: string): string {
  if (status === 0) return 'The upload stopped. Check the internet connection and try again.';
  if (/Policy expired|Request has expired/i.test(body)) return 'The upload took too long and its permission expired. Try again.';
  if (/EntityTooLarge/i.test(body)) return `The file is too big. The limit is ${VIDEO_UPLOAD_MAX_BYTES / GB} GB.`;
  return `The upload did not finish (error ${status}). Try again.`;
}

/** The "ready" message, shown and spoken with the same words. */
export function readyText(languageName: string): string {
  return `The new ${languageName} video is ready. Check it in the preview. Then publish.`;
}

/**
 * The words of the panel's polite live region: phase changes and 10 % steps
 * only (a screen reader that read every percent would read nothing else),
 * and "ready" at the end — which is how a person who is typing somewhere else
 * learns it, because focus is not moved then (see shouldTakeFocus).
 */
export function spokenText(s: UploadState, languageName: string): string {
  if (s.phase === 'checking') return 'Checking the file.';
  if (s.phase === 'uploading') return `Uploading. ${s.total > 0 ? Math.floor((s.loaded / s.total) * 10) * 10 : 0} %`;
  if (s.phase === 'processing') return s.percent === null ? 'Processing.' : `Processing. ${Math.floor(s.percent / 10) * 10} %`;
  if (s.phase === 'done') return readyText(languageName);
  return '';
}

/**
 * May the "ready" moment move focus to the ready message? Only when focus is on
 * nothing (null or the page body) or already inside this panel. Anywhere else
 * the admin is doing something; moving focus then breaks WCAG 3.2.2 (On Input)
 * and 2.4.3 (Focus Order), so the live region says "ready" instead.
 */
export function shouldTakeFocus(
  active: Element | null,
  body: Element | null,
  panel: { contains(node: Node | null): boolean } | null,
): boolean {
  if (active === null || active === body) return true;
  return panel !== null && panel.contains(active);
}
